import 'styles/global.css';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTheme } from 'contexts/ThemeContext';
import { getUserRole } from 'utils/auth/auth';
import Sidebar from 'components/navigation/Sidebar';
import ConnectingBanner from 'components/common/ConnectingBanner';
import Subscriptions from 'pages/subscriptions/Subscriptions';
import OnboardingWizard from 'pages/onboarding/OnboardingWizard';
import AccountsLayout from './AccountsLayout';
import Messages from 'pages/actions/Messages';
import Comments from 'pages/actions/Comments';
import Likes from 'pages/actions/Likes';
import Followings from 'pages/actions/Followings';
import Posts from 'pages/actions/Posts';
import Story from 'pages/actions/Story';
import Settings from 'pages/settings/Settings';
import Help from 'pages/help/Help';
import Support from 'pages/support/Support';
import LandingDashboard from 'pages/dashboard/Dashboard';
import Admin from 'pages/admin/AdminDashboard';
import SubscriptionManagement from 'pages/admin/SubscriptionManagement';
import AccountsAnalysis from 'pages/admin/AccountsAnalysis';
import { UserManagement, AccountsManagement, JobManagement, QueueManagement, AnboxManagement, ProxiesManagement } from 'pages/superAdmin';
import Rules from 'pages/rules/Rules';
import NewRule from 'pages/rules/NewRule';
import RuleDetail from 'pages/rules/RuleDetail';
import Logout from 'pages/logout/Logout';
import SubscriptionExpired from 'pages/subscriptions/SubscriptionExpired';

import Billing from 'pages/billing/Billing';
import PaymentSuccess from 'pages/subscriptions/PaymentSuccess';
import PaymentCancel from 'pages/subscriptions/PaymentCancel';
import { RootState } from 'config/redux/store';
import { SidebarProvider } from 'contexts/SidebarContext';
import { getAccessToken } from 'utils/auth/auth';

// Define interface for component props
interface DashboardProps {
  setIsLoggedIn: (isLoggedIn: boolean) => void;
}

// Define component
const DashboardLayout: React.FC<DashboardProps> = ({ setIsLoggedIn }) => {
  const token = getAccessToken();
  const location = useLocation();
  const { isExpired, selectedUser, expiryDate } = useSelector((state: RootState) => state.user);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const { isDark } = useTheme();

  // Admins and super-admins are never subscription-gated. Mirrors SubscriptionGuard's
  // route-level bypass — without this, an admin with isExpired=true on a non-listed path
  // (e.g. /dashboard) would render nothing because neither the wizard nor the expired-only
  // page list applies to them.
  const role = (getUserRole() || '').toLowerCase().replace(/\s+/g, '');
  const isAdminUser = role === 'admin' || role === 'superadmin';
  const treatAsExpired = isExpired === true && !isAdminUser;

  useEffect(() => {
    if (!token) {
      window.location.href = '/login';
    }
  }, [token]);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  return (
    <SidebarProvider value={{ toggleSidebar }}>
    <div className="h-screen overflow-hidden" style={{ background: isDark ? '#0F172B' : '#F8FAFC' }}>
      <Sidebar
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        setIsLoggedIn={setIsLoggedIn}
      />

      {/* Main Content */}
      <main
        className="fixed top-0 bottom-0 left-0 right-0 lg:left-[200px] flex flex-col overflow-hidden"
        style={{ background: isDark ? '#0F172B' : '#F8FAFC' }}
      >
        {/* Persistent Instagram connecting banner — fixed at top, never scrolls */}
        <div className="flex-shrink-0">
          <ConnectingBanner />
        </div>
        {/* Scrollable content area */}
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {(() => {
          // For an expired (no active subscription) non-admin user, these pages render normally.
          // Every OTHER path renders an in-place takeover (URL unchanged) so the user can still
          // see Billing / Help / Support but anything else is gated:
          //   • brand-new signup (no prior payment, expiryDate=null) → OnboardingWizard (trial CTA)
          //   • returning expired user (had a previous payment) → SubscriptionExpired (renew CTA)
          const renderActualPageWhenExpired = [
            '/billing', '/help', '/support', '/settings', '/subscriptions',
            '/payment/success', '/payment/cancel', '/logout'
          ];

          if (treatAsExpired && !renderActualPageWhenExpired.includes(location.pathname)) {
            return expiryDate ? <SubscriptionExpired /> : <OnboardingWizard />;
          }
          return null;
        })()}
        {treatAsExpired ? (
          ['/billing', '/help', '/support', '/settings', '/subscriptions', '/payment/success', '/payment/cancel', '/admin', '/admin/subscriptions', '/admin/accounts-analysis', '/super-admin/users', '/super-admin/accounts', '/super-admin/jobs', '/super-admin/queue', '/super-admin/anbox', '/super-admin/proxies'].includes(location.pathname) ? (
            <>
              {location.pathname === '/billing' && <Billing />}
              {location.pathname === '/help' && <Help />}
              {location.pathname === '/support' && <Support />}
              {location.pathname === '/settings' && <Settings />}
              {location.pathname === '/subscriptions' && <Subscriptions />}
              {location.pathname === '/payment/success' && <PaymentSuccess />}
              {location.pathname === '/payment/cancel' && <PaymentCancel />}
              {location.pathname === '/admin' && <Admin />}
              {location.pathname === '/admin/subscriptions' && <SubscriptionManagement />}
              {location.pathname === '/admin/accounts-analysis' && <AccountsAnalysis />}
              {location.pathname === '/super-admin/users' && <UserManagement />}
              {location.pathname === '/super-admin/accounts' && <AccountsManagement />}
              {location.pathname === '/super-admin/jobs' && <JobManagement />}
              {location.pathname === '/super-admin/queue' && <QueueManagement />}
              {location.pathname === '/super-admin/anbox' && <AnboxManagement />}
              {location.pathname === '/super-admin/proxies' && <ProxiesManagement />}
            </>
          ) : (
            // Everything else for expired users renders the wizard in place (already returned above).
            null
          )
        ) : (
          <>
            {location.pathname === '/dashboard' && <LandingDashboard />}
            {location.pathname === '/accounts' && <AccountsLayout />}
            {location.pathname.includes('/accounts/') &&
              location.pathname.includes('/tabs') && <AccountsLayout />}
            {location.pathname === '/rules' && <Rules />}
            {location.pathname === '/rules/new' && <NewRule />}
            {location.pathname.startsWith('/rules/') && location.pathname !== '/rules/new' && <RuleDetail />}
            {location.pathname === '/actions/posts' && <Posts selectedUser={selectedUser ?? ''} />}
            {location.pathname === '/actions/story' && <Story selectedUser={selectedUser ?? ''} />}
            {location.pathname === '/actions/messages' && <Messages selectedUser={selectedUser ?? ''} />}
            {location.pathname === '/actions/comments' && <Comments selectedUser={selectedUser ?? ''} />}
            {location.pathname === '/actions/likes' && <Likes selectedUser={selectedUser ?? ''} />}
            {location.pathname === '/actions/followings' && <Followings selectedUser={selectedUser ?? ''} />}
            {location.pathname === '/subscriptions' && <Subscriptions />}

            {location.pathname === '/billing' && <Billing />}
            {location.pathname === '/payment/success' && <PaymentSuccess />}
            {location.pathname === '/payment/cancel' && <PaymentCancel />}
            {location.pathname === '/admin' && <Admin />}
            {location.pathname === '/admin/subscriptions' && <SubscriptionManagement />}
            {location.pathname === '/admin/accounts-analysis' && <AccountsAnalysis />}
            {location.pathname === '/super-admin/users' && <UserManagement />}
            {location.pathname === '/super-admin/accounts' && <AccountsManagement />}
            {location.pathname === '/super-admin/jobs' && <JobManagement />}
            {location.pathname === '/super-admin/queue' && <QueueManagement />}
            {location.pathname === '/super-admin/anbox' && <AnboxManagement />}
            {location.pathname === '/super-admin/proxies' && <ProxiesManagement />}
            {location.pathname === '/settings' && <Settings />}
            {location.pathname === '/help' && <Help />}
            {location.pathname === '/support' && <Support />}
            {location.pathname === '/logout' && <Logout />}
          </>
        )}
        </div>
      </main>
    </div>
    </SidebarProvider>
  );
};

export default DashboardLayout;
